import React, { useEffect } from 'react';
import { Shield, Lock, Eye, UserCheck, Database, Bell } from 'lucide-react';

const PrivacyPolicyPage = () => {
  useEffect(() => {
    document.title = 'Privacy Policy | Indian Cyber Club Academy';
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-black min-h-screen pt-20 sm:pt-24 pb-16">
      <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 mb-4">
            <Lock className="w-5 h-5 text-red-500" />
            <span className="text-red-500 font-semibold text-sm uppercase tracking-wider">Legal Policy</span>
          </div>
          <h1 className="text-4xl lg:text-5xl font-black text-white mb-4">
            Privacy <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-orange-500">Policy</span>
          </h1>
          <p className="text-gray-400">Last Updated: January 30, 2025</p>
        </div>

        {/* Intro */}
        <div className="bg-gradient-to-br from-gray-900 to-gray-950 border border-gray-800 rounded-xl p-8 mb-8">
          <p className="text-gray-300 leading-relaxed">
            At <strong className="text-white">Indian Cyber Club Academy</strong>, we are committed to protecting your privacy and personal information. This Privacy Policy explains how we collect, use, store, and protect your data when you use our website and services.
          </p>
        </div>

        {/* Policy Content */}
        <div className="space-y-8">
          {/* Section 1 */}
          <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-8">
            <h2 className="text-2xl font-black text-white mb-4 flex items-center gap-2">
              <Database className="w-6 h-6 text-red-500" />
              Information We Collect
            </h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              We collect the following types of information:
            </p>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-bold text-white mb-2">Personal Information</h3>
                <ul className="space-y-2">
                  {['Name, email address, phone number', 'Age, gender, and address', 'Educational background and qualifications', 'Payment and billing information', 'Government-issued ID (for certificate verification)'].map((item, index) => (
                    <li key={index} className="flex items-start gap-3 text-gray-300 text-sm">
                      <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0 mt-2"></span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-bold text-white mb-2">Technical Information</h3>
                <ul className="space-y-2">
                  {['IP address, browser type, and device information', 'Cookies and usage data', 'Pages visited and time spent on website', 'Referral source and clickstream data'].map((item, index) => (
                    <li key={index} className="flex items-start gap-3 text-gray-300 text-sm">
                      <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0 mt-2"></span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Section 2 */}
          <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-8">
            <h2 className="text-2xl font-black text-white mb-4 flex items-center gap-2">
              <Eye className="w-6 h-6 text-red-500" />
              How We Use Your Information
            </h2>
            <ul className="space-y-3">
              {[
                'To process course enrollments and payments',
                'To provide course materials, updates, and support',
                'To communicate important information about your courses',
                'To issue certificates upon course completion',
                'To send promotional offers and updates (with your consent)',
                'To improve our website, courses, and services',
                'To analyze website usage and user behavior',
                'To comply with legal obligations and prevent fraud'
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3 text-gray-300">
                  <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0 mt-2"></span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Section 3 */}
          <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-8">
            <h2 className="text-2xl font-black text-white mb-4 flex items-center gap-2">
              <UserCheck className="w-6 h-6 text-red-500" />
              Information Sharing
            </h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              We do <strong>NOT</strong> sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
            </p>
            <ul className="space-y-3">
              {[
                'With payment processors to complete transactions',
                'With email service providers for course communications',
                'With certification partners for credential issuance',
                'With placement partners (with your explicit consent)',
                'When required by law or legal processes',
                'To protect our rights, property, or safety'
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3 text-gray-300">
                  <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0 mt-2"></span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Section 4 */}
          <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-8">
            <h2 className="text-2xl font-black text-white mb-4 flex items-center gap-2">
              <Shield className="w-6 h-6 text-red-500" />
              Data Security
            </h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              We implement industry-standard security measures to protect your personal information:
            </p>
            <ul className="space-y-3">
              {[
                'SSL encryption for data transmission',
                'Secure servers and databases',
                'Access controls and authentication',
                'Regular security audits and updates',
                'Employee training on data protection'
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3 text-gray-300">
                  <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0 mt-2"></span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-gray-400 text-sm mt-4">
              While we strive to protect your information, no method of transmission over the internet is 100% secure. We cannot guarantee absolute security.
            </p>
          </div>

          {/* Section 5 */}
          <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-8">
            <h2 className="text-2xl font-black text-white mb-4">Cookies and Tracking</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              We use cookies and similar technologies to enhance your experience:
            </p>
            <ul className="space-y-3">
              {[
                'Essential cookies for website functionality',
                'Analytics cookies to understand user behavior',
                'Marketing cookies for targeted advertising (with consent)',
                'Session cookies to maintain your login state'
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3 text-gray-300">
                  <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0 mt-2"></span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-gray-400 text-sm mt-4">
              You can control cookie preferences through your browser settings.
            </p>
          </div>

          {/* Section 6 */}
          <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-8">
            <h2 className="text-2xl font-black text-white mb-4">Your Rights</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              You have the following rights regarding your personal data:
            </p>
            <ul className="space-y-3">
              {[
                'Access: Request a copy of your personal data',
                'Correction: Request correction of inaccurate information',
                'Deletion: Request deletion of your data (subject to legal obligations)',
                'Objection: Object to processing of your data for marketing purposes',
                'Portability: Request transfer of your data to another service',
                'Withdraw Consent: Opt-out of marketing communications at any time'
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3 text-gray-300">
                  <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0 mt-2"></span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="text-gray-400 text-sm mt-4">
              To exercise these rights, please contact us at +91-9694612895.
            </p>
          </div>

          {/* Section 7 */}
          <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-8">
            <h2 className="text-2xl font-black text-white mb-4">Data Retention</h2>
            <p className="text-gray-300 leading-relaxed">
              We retain your personal information for as long as necessary to provide our services and comply with legal obligations. Student records and certificates are retained indefinitely for verification purposes.
            </p>
          </div>

          {/* Section 8 */}
          <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-8">
            <h2 className="text-2xl font-black text-white mb-4">Third-Party Links</h2>
            <p className="text-gray-300 leading-relaxed">
              Our website may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. We recommend reviewing their privacy policies before providing any personal information.
            </p>
          </div>

          {/* Section 9 */}
          <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-8">
            <h2 className="text-2xl font-black text-white mb-4">Children's Privacy</h2>
            <p className="text-gray-300 leading-relaxed">
              Our services are not intended for children under 16 years of age. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.
            </p>
          </div>

          {/* Section 10 */}
          <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-8">
            <h2 className="text-2xl font-black text-white mb-4">Changes to Privacy Policy</h2>
            <p className="text-gray-300 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of significant changes by posting the new policy on this page and updating the "Last Updated" date. We encourage you to review this policy periodically.
            </p>
          </div>

          {/* Contact */}
          <div className="bg-gradient-to-br from-red-600/10 to-orange-600/10 border-2 border-red-500/30 rounded-xl p-8">
            <h2 className="text-2xl font-black text-white mb-4 flex items-center gap-2">
              <Bell className="w-6 h-6 text-red-500" />
              Contact Us
            </h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              If you have any questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="bg-gray-950/50 border border-red-600/30 rounded-lg p-4 space-y-2">
              <p className="text-white">
                <strong>Indian Cyber Club Academy</strong>
              </p>
              <p className="text-gray-300">
                Phone: <a href="tel:+919694612895" className="text-red-500 hover:underline">+91-9694612895</a>
              </p>
              <p className="text-gray-300">
                WhatsApp: <a href="https://wa.me/919694612895" target="_blank" rel="noopener noreferrer" className="text-red-500 hover:underline">+91-9694612895</a>
              </p>
              <p className="text-gray-300">
                Location: Jaipur, Rajasthan, India
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
